<script setup>
import BreezeButton from '@/Components/Button.vue';
import BreezeGuestLayout from '@/Layouts/Guest.vue';
import BreezeInput from '@/Components/Input.vue';
import BreezeLabel from '@/Components/Label.vue';
import BreezeValidationErrors from '@/Components/ValidationErrors.vue';
import { Head, useForm } from '@inertiajs/vue3';
import { ref, onMounted } from 'vue'

const props = defineProps({
    companyLogo: String,
    twoFactorEnabled: Boolean,
    enabling: Boolean,
    qrCode: String,
});

const confirmationForm = useForm({
    code: '',
});

const qrCode = ref(props.qrCode);
const twoFactorEnabled = ref(props.twoFactorEnabled);
const enabling = ref(props.enabling);
const confirming = ref(false);
const setupKey = ref(null);
const recoveryCodes = ref([]);

const showQrCode = () => {
    return axios.get('/user/two-factor-qr-code')
        .then(response => {
            qrCode.value = response.data.svg
        })
};

const enableTwoFactorAuthentication = () => {
    if (twoFactorEnabled.value === true) {
        return;
    }

    if (enabling.value === true) {
        showQrCode()
        showSetupKey()
        return
    }

    axios.post('/user/two-factor-authentication')
    .then(() => {
        return Promise.all([
            showQrCode(),
            showSetupKey(),
            ])
    })
};

const confirmTwoFactorAuthentication = () => {
    confirmationForm.post(route('two-factor.confirm'), {
        errorBag: "confirmTwoFactorAuthentication",
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            confirming.value = false;
            qrCode.value = null;
            twoFactorEnabled.value = true;
            setupKey.value = null;
            showRecoveryCodes();
        },
    });
};

const showRecoveryCodes = () => {
    return axios.get('/user/two-factor-recovery-codes').then(response => {
        recoveryCodes.value = response.data;
    });
};

const regenerateRecoveryCodes = () => {
    axios.post('/user/two-factor-recovery-codes')
        .then(() => showRecoveryCodes());
};

const showSetupKey = () => {
    return axios.get('/user/two-factor-secret-key').then(response => {
        setupKey.value = response.data.secretKey;
    });
}

const disableTwoFactorAuthentication = () => {
    axios.delete('/user/two-factor-authentication')
        .then(() => {
            enabling.value = false;
            confirming.value = false;
            twoFactorEnabled.value = false;
            qrCode.value = null;
            enableTwoFactorAuthentication();
        })
}

onMounted(() => {
    enableTwoFactorAuthentication()
});

</script>

<style scoped>
    #qr-code :deep(svg) {
        margin-right: auto;
        margin-left: auto;
    }
</style>

<template>
    <BreezeGuestLayout :companyLogo="companyLogo">
        <Head title="Manage Two Factor Authentication" />

        <div class="mb-4 text-sm text-gray-600">
            Manage Two Factor Authentication
        </div>

        <div id="qr-code" class="h-56 grid grid-cols-1 gap-4 content-center" v-if="qrCode" v-html="qrCode" />

        <div class="mt-4 max-w-xl text-sm text-gray-600" v-if="setupKey">
            <p class="font-semibold">
                Setup Key: <span v-html="setupKey"></span>
            </p>
        </div>

        <div v-if="recoveryCodes.length > 0 && ! confirming">
            <div class="mt-4 max-w-xl text-sm text-gray-600">
                <p class="font-semibold">
                    Store these recovery codes in a secure password manager. They can be used to recover access to your account if your two factor authentication device is lost.
                </p>
            </div>
            <div class="grid gap-1 max-w-xl mt-4 px-4 py-4 font-mono text-sm bg-gray-100 rounded-lg">
                <div v-for="code in recoveryCodes" :key="code">
                    {{ code }}
                </div>
            </div>
        </div>

        <div class="flex mt-4" v-if="twoFactorEnabled">
            <p class="font-semibold">Two Factor is ENABLED </p>
        </div>
        <div class="flex justify-end mt-4">
            <BreezeButton @click.prevent="regenerateRecoveryCodes" v-if="twoFactorEnabled" class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest focus:outline-none focus:ring disabled:opacity-25 transition">
                Regenerate Recovery Codes
            </BreezeButton>
            <BreezeButton @click.prevent="disableTwoFactorAuthentication" v-if="twoFactorEnabled" class="ml-4 inline-flex items-center justify-center px-4 py-2 bg-red-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-red-500 focus:outline-none focus:border-red-700 focus:ring focus:ring-red-200 active:bg-red-600 disabled:opacity-25 transition">
                Disable
            </BreezeButton>
        </div>

        <BreezeValidationErrors class="mb-4" />

        <form v-if="! twoFactorEnabled" @submit.prevent="confirmTwoFactorAuthentication">

            <div>
                <BreezeLabel class="text-xs" for="code" value="Enter the 6-digit code from your authenticator app below" />
                <BreezeInput id="code" type="text" inputmode="numeric" class="mt-1 block w-full" v-model="confirmationForm.code" required autocomplete="one-time-code" autofocus @keyup.enter="confirmTwoFactorAuthentication" />
            </div>

            <div class="flex justify-end mt-4">
                <BreezeButton v-if="! twoFactorEnabled" class="ml-4" :class="{ 'opacity-25': confirmationForm.processing }" :disabled="confirmationForm.processing">
                    Enable Two Factor Authentication
                </BreezeButton>
            </div>
        </form>
    </BreezeGuestLayout>
</template>
